

import {
  CreateBlankWorkflow, GetVideoEntryForWorkflowStep, Instructions, Workflow, WorkflowStage, WorkflowStep,
} from '@/model/Workflow';
import Utils from '@/services/Utils';
import workflowManager, { WorkflowManager } from '@/services/WorkflowManager';
import WorkflowMenu from '@/components/screens/WorkflowMenu.vue';
import {
  computed, defineComponent, nextTick, ref, watchEffect,
} from 'vue';
import motionDb from '@/services/MotionDatabase';
import MiniLesson from '@/model/MiniLesson';
import CreateLessonScreen from '@/components/screens/CreateLessonScreen.vue';

export default defineComponent({
  name: 'CreateWorkflowScreen',
  emits: ['back-selected', 'workflow-created'],
  components: {
    WorkflowMenu,
    CreateLessonScreen,
  },
  setup() {

    const isDirty = ref(false);

    const activeWorkflow = ref(null as null | Workflow);
    const selectingWorkflowIndex = ref(-1);
    const canEditWorkflow = computed(() => {
      const workflow = workflowManager.allWorkflows.value[selectingWorkflowIndex.value];
      return workflow && workflowManager.isCustomWorkflow(workflow.id);
    });
    const workflowInDatabase = computed(() => activeWorkflow.value?.id && WorkflowManager.hasSavedCustomLesson(activeWorkflow.value.id));
    const selectedStageIndex = ref(0);
    const activeStage = computed(() => activeWorkflow.value?.stages[selectedStageIndex.value] ?? null);
    const selectedStepIndex = ref(0);
    const activeStep = computed(() => activeStage.value?.steps[selectedStepIndex.value] ?? null);
    const isVideoUploadStep = computed(() => activeStep.value?.type === 'UploadTask');
    const isLessonReferenceStep = computed(() => activeStep.value?.type === 'MiniLessonReference');
    const isLessonEmbeddedStep = computed(() => activeStep.value?.type === 'MiniLessonEmbedded');
    const isInstructionStep = computed(() => activeStep.value?.type === 'InstructionOnly');
    const availableReferenceLessons = computed(() => {
      if (!activeStep.value) return [] as MiniLesson[];
      if (!activeStep.value.miniLessonReference?.clipName) return [] as MiniLesson[];
      return motionDb.lessonsByVideo.get(activeStep.value.miniLessonReference.clipName) ?? [];
    });
    const activeStepLesson = computed(() => {
      if (!activeStep.value) return null;
      if (isLessonReferenceStep.value && activeStep.value.miniLessonReference?.lessonId) return motionDb.lessonsById.get(activeStep.value.miniLessonReference.lessonId) ?? null;
      if (isLessonEmbeddedStep.value) return activeStep.value.miniLessonEmbedded ?? null;
      return null;
    });
    const canEditActiveStepLessonReference = computed(() => activeStepLesson.value?.source === 'custom');
    const newEmbeddedLessonClipName = ref('');
    const newEmbeddedLessonMotion = computed(() => motionDb.motionsMap.get(newEmbeddedLessonClipName.value) ?? null);
    const canCreateEmbeddedLesson = computed(() => newEmbeddedLessonMotion.value !== null);
    const editLessonActive = ref(false);
    const embeddedLessonMotion = computed(() => {
      if (!activeStep.value) return null;
      if (isLessonEmbeddedStep.value && activeStep.value.miniLessonEmbedded) return GetVideoEntryForWorkflowStep(motionDb, activeStep.value);
      if (isLessonEmbeddedStep.value) return newEmbeddedLessonMotion.value;
      if (isLessonReferenceStep.value) return GetVideoEntryForWorkflowStep(motionDb, activeStep.value);
      return null;
    });

    watchEffect(() => {
      if (activeWorkflow.value) {
        workflowManager.setActiveFlow(activeWorkflow.value.id);
        workflowManager.completeActivitesPriorToStageAndStep(selectedStageIndex.value, selectedStepIndex.value);
        // TODO: improve logic to keep status of intra-stage workflow steps.
      }
    });

    watchEffect(() => {
      if (!activeStep.value) return;
      if (isInstructionStep.value) {
        activeStep.value.instructions = activeStep.value.instructions ?? {
          heading: 'Instructions Heading',
          text: '',
        } as Instructions;
      } else if (isLessonReferenceStep.value) {
        activeStep.value.miniLessonReference = activeStep.value.miniLessonReference ?? {} as any;
        //  ?? {

        // };
      } else if (isLessonEmbeddedStep.value) {
        // activeStep.value.miniLessonEmbedded = activeStep.value.miniLessonEmbedded;
        //  ?? {

        // };
      } else if (isVideoUploadStep.value) {
        activeStep.value.upload = activeStep.value.upload ?? {
          identifier: activeStep.value.title,
          prompt: 'Upload your video here',
        };
      }
    });

    return {
      selectingWorkflowIndex,
      activeWorkflow,
      canEditWorkflow,
      workflowInDatabase,
      availableWorkflows: workflowManager.allWorkflows,
      selectedStageIndex,
      activeStage,

      selectedStepIndex,
      activeStep,
      isVideoUploadStep,
      isLessonReferenceStep,
      isLessonEmbeddedStep,
      isInstructionStep,
      availableClips: motionDb.motionNames,
      availableReferenceLessons,
      newEmbeddedLessonClipName,
      newEmbeddedLessonMotion,
      embeddedLessonMotion,
      canCreateEmbeddedLesson,
      editLessonActive,
      activeStepLesson,
      canEditActiveStepLessonReference,
      isMoveStepDropdownActive: ref(false),

      isDirty,
    };
  },
  data() {
    return {
    };
  },
  watch: {
    activeWorkflow: {
      handler(newVal) {
        console.log('Workflow now dirty');
        this.isDirty = ((newVal ?? null) !== null);
      },
      deep: true,
    },
  },
  computed: {
  },
  methods: {
    goBack(skipPrompt?: boolean) {
      if (!this.activeWorkflow) {
        this.$emit('back-selected');
        return;
      }

      if (skipPrompt || !this.isDirty) {
        this.activeWorkflow = null;
        return;
      }

      // eslint-disable-next-line no-alert
      if (window.confirm('Go back without saving changes?')) {
        workflowManager.reloadCustomWorkflow(this.activeWorkflow.id);
        this.activeWorkflow = null;
      }
    },
    deleteWorkflow() {
      if (!this.activeWorkflow) return;

      // eslint-disable-next-line no-alert
      if (!window.confirm('Are you sure you want to delete this lesson?')) {
        return;
      }

      workflowManager.deleteCustomWorkflow(this.activeWorkflow.id);
      this.goBack(true);
    },
    exportWorkflow() {
      if (!this.activeWorkflow) return;
      Utils.PromptDownloadFile(`${this.activeWorkflow.title}.workflow.json`, JSON.stringify(this.activeWorkflow));
    },
    saveWorkflow() {
      if (!this.activeWorkflow) return;
      console.log('Saving workflow');
      workflowManager.upsertCustomWorkflow(this.activeWorkflow);
      nextTick(() => {
        this.isDirty = false;
        console.log('Workflow saved; not dirty now');
      });
      // window.setTimeout(() => {
      // }, 200);
    },
    startCreation(asTemplate: boolean) {
      const existingWorkflow = this.availableWorkflows[this.selectingWorkflowIndex];
      if (existingWorkflow && asTemplate) {
        this.activeWorkflow = Utils.deepCopy(existingWorkflow);
        this.activeWorkflow.id = Utils.uuidv4();
      } else if (existingWorkflow && !asTemplate) {
        this.activeWorkflow = existingWorkflow;
        nextTick(() => {
          this.isDirty = false;
        });

      } else {
        this.activeWorkflow = CreateBlankWorkflow();
      }

      workflowManager.addSessionWorkflow(this.activeWorkflow);
      workflowManager.setActiveFlow(this.activeWorkflow.id);
    },
    selectStage(stageIndex: number) {

      // Clear selection if this stage was already selected
      if (stageIndex === this.selectedStageIndex) {
        this.selectedStageIndex = -1;
        this.selectedStepIndex = -1;
        return;
      }

      this.selectedStageIndex = stageIndex;
      this.selectedStepIndex = -1;
      workflowManager.completeActivitesPriorToStageAndStep(stageIndex, this.selectedStepIndex);
    },
    canReorderStage(increment: number) {
      const workflow = this.activeWorkflow;
      if (!workflow) return false;
      const { stages } = workflow;
      const curStage = stages[this.selectedStageIndex];
      const swapStage = stages[this.selectedStageIndex + increment];
      return (curStage !== undefined) && (swapStage !== undefined);
    },
    reorderStage(increment: number) {
      const workflow = this.activeWorkflow;
      if (!workflow) return;
      const { stages } = workflow;
      const swapIndex = this.selectedStageIndex + increment;
      const curSelected = stages[this.selectedStageIndex];
      const toSwap = stages[swapIndex];
      if ((curSelected === undefined) || (toSwap === undefined)) {
        console.warn(`Unable to swap stages ${this.selectedStageIndex} and ${swapIndex}`);
        return;
      }
      stages[this.selectedStageIndex] = toSwap;
      stages[swapIndex] = curSelected;
      this.selectedStageIndex = swapIndex;
      workflow.stages = stages;
    },
    addStage() {
      const workflow = this.activeWorkflow;
      if (!workflow) return;

      workflow.stages.push({
        title: `New-Stage-${workflow.stages.length + 1}`,
        steps: [],
      } as WorkflowStage);
      this.selectStage(workflow.stages.length - 1);
    },
    duplicateStage() {
      const workflow = this.activeWorkflow;
      if (!workflow) return;
      const { stages } = workflow;
      const curStage = stages[this.selectedStageIndex];
      if (!curStage) return;
      stages.splice(this.selectedStageIndex, 0, Utils.deepCopy(curStage));
      workflow.stages = stages;
      this.selectedStageIndex += 1;
    },
    deleteStage() {
      const workflow = this.activeWorkflow;
      if (!workflow) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Are you sure you want to delete Stage #${this.selectedStageIndex + 1}?`)) return;

      const { stages } = workflow;
      stages.splice(this.selectedStageIndex, 1);
      workflow.stages = stages;
      this.selectedStageIndex = Math.min(this.selectedStageIndex, stages.length - 1);
    },
    selectStep(stepIndex: number) {

      // Clear selectedStep if already selected
      if (this.selectedStepIndex === stepIndex) {
        this.selectedStepIndex = -1;
        return;
      }
      this.selectedStepIndex = stepIndex;

      workflowManager.completeActivitesPriorToStageAndStep(this.selectedStageIndex, this.selectedStepIndex);
    },
    canReorderStep(increment: number) {
      const workflow = this.activeWorkflow;
      if (!workflow) return false;
      const steps = workflow.stages[this.selectedStageIndex]?.steps;
      if (!steps) return false;
      const curStep = steps[this.selectedStepIndex];
      const swapStep = steps[this.selectedStepIndex + increment];
      return (curStep !== undefined) && (swapStep !== undefined);
    },
    reorderStep(increment: number) {
      const workflow = this.activeWorkflow;
      if (!workflow) return;
      const steps = workflow.stages[this.selectedStageIndex]?.steps;
      if (!steps) return;
      const swapIndex = this.selectedStepIndex + increment;
      const curStep = steps[this.selectedStepIndex];
      const swapStep = steps[swapIndex];
      if ((curStep === undefined) || (swapStep === undefined)) {
        console.warn(`Unable to swap steps ${this.selectedStepIndex} and ${swapIndex}`);
        return;
      }
      steps[this.selectedStageIndex] = swapStep;
      steps[swapIndex] = curStep;
      this.selectedStepIndex = swapIndex;
      workflow.stages[this.selectedStageIndex].steps = steps;
    },
    addStep() {
      const stage = this.activeStage;
      if (!stage) return;

      stage.steps.push({
        title: `New-Step-${stage.steps.length + 1}`,
        type: 'InstructionOnly',
      } as WorkflowStep);
      this.selectStep(stage.steps.length - 1);
    },
    duplicateStep() {
      const workflow = this.activeWorkflow;
      if (!workflow) return;
      const curStep = this.activeWorkflow?.stages[this.selectedStageIndex]?.steps[this.selectedStepIndex];
      if (!curStep) return;
      const { steps } = workflow.stages[this.selectedStageIndex];
      steps.splice(this.selectedStepIndex, 0, Utils.deepCopy(curStep));
      workflow.stages[this.selectedStageIndex].steps = steps;
      this.selectedStepIndex += 1;
    },
    moveStep(targetStageIndex: number) {
      if (targetStageIndex === this.selectedStageIndex) return;
      const stage = this.activeStage;
      const stepToMove = this.activeStep;
      const targetPhase = this.activeWorkflow?.stages[targetStageIndex];
      if (!stage || !stepToMove || !targetPhase) return;
      const { steps } = stage;
      steps.splice(this.selectedStepIndex, 1);
      stage.steps = steps;
      targetPhase.steps.push(stepToMove);
      this.selectedStageIndex = targetStageIndex;
      this.selectedStepIndex = this.activeStage?.steps.length ?? 0;
      this.isMoveStepDropdownActive = false;
    },
    deleteStep() {
      const stage = this.activeStage;
      if (!stage) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Are you sure you want to delete Step #${this.selectedStepIndex + 1}?`)) return;

      const { steps } = stage;
      steps.splice(this.selectedStepIndex, 1);
      stage.steps = steps;
      this.selectedStepIndex = Math.min(this.selectedStepIndex, steps.length - 1);
    },
    updateWorkflowStepLesson(lesson: MiniLesson) {
      if (!this.activeStep) return;
      if (this.isLessonEmbeddedStep) {
        this.activeStep.miniLessonEmbedded = lesson;
        console.log('Updated embedded lesson', lesson.header.lessonTitle);
      }
      if (this.isLessonReferenceStep && this.activeStep.miniLessonReference) {
        this.activeStep.miniLessonReference.lessonId = lesson._id;
        console.log('Updated reference lesson', lesson);
      }
    },
    startCreateEmbeddedLesson() {
      if (!this.activeStep) return;
      if (!this.newEmbeddedLessonMotion) return;
      // this.activeStep.miniLessonEmbedded = createBlankLesson(this.newEmbeddedLessonMotion);
      // this.activeStep.miniLessonEmbedded.header.lessonTitle = this.activeStep.title;
      this.editLessonActive = true;
    },
    createReferencedLesson() {
      if (!this.isLessonReferenceStep) return;
      if (this.activeStep?.miniLessonReference?.lessonId === '') {
        this.editLessonActive = true;
      }
    },
    editWorkflowStepLesson() {
      if (!this.activeStepLesson) return;
      this.editLessonActive = true;
    },
    removeEmbeddedLesson() {
      if (!this.activeStep?.miniLessonEmbedded) return;
      // eslint-disable-next-line no-alert
      if (window.confirm(`Are you sure you want to delete the embedded lesson ${this.activeStep.miniLessonEmbedded.header.lessonTitle}?`)) {
        this.activeStep.miniLessonEmbedded = undefined;
      }
    },
    convertEmbeddedLessonToReference() {
      if (!this.activeStep?.miniLessonEmbedded) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm('Are you sure you want to save this lesson into a new file and convert this to a reference?'
                         + '\n\nNote: be sure to save the lesson.json!')
      ) return;

      motionDb.saveCustomLesson(this.activeStep.miniLessonEmbedded);
      this.activeStep.miniLessonReference = {
        clipName: this.activeStep.miniLessonEmbedded.header.clipName,
        lessonId: this.activeStep.miniLessonEmbedded._id,
      };
      const exportLessonData = this.activeStep.miniLessonEmbedded;
      this.activeStep.type = 'MiniLessonReference';
      this.activeStep.miniLessonEmbedded = undefined;
      Utils.PromptDownloadFile(`${exportLessonData.header.lessonTitle}.lesson.json`, JSON.stringify(exportLessonData));
    },
  },
});
